import { Component, inject, Inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SharedModule } from '../../../../../shared/shared.module';
import {
  IContactWidget,
  IManualContactCreateData
} from '../../../../interfaces/contact-widget.interface';
import { ManualContact } from '../../../../interfaces/contact.interface';
import { ContactWidgetService } from '../../../../services/contact-widget.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-manual-contact-create',
  imports: [SharedModule],
  templateUrl: './manual-contact-create.component.html',
  styleUrl: './manual-contact-create.component.scss'
})
export class ManualContactCreateComponent {
  // Services
  private readonly contactWidgetService: ContactWidgetService = inject(ContactWidgetService);
  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly translate: TranslateService = inject(TranslateService);

  contactName: string = '';
  phoneNumber: string = '';
  contactWidget: IContactWidget;

  constructor(
      public dialogRef: MatDialogRef<ManualContactCreateComponent>,
      @Inject(MAT_DIALOG_DATA) public data: IManualContactCreateData,
  ) {
    this.contactWidget = data.widget;
  }

  isFormValid(): boolean {
    return this.contactName.trim().length >= 2 &&
        this.phoneNumber.trim().length > 0;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async save(): Promise<void> {
    if (!this.isFormValid()) {
      return;
    }

    if (!this.contactWidget?._id) {
      console.error('[ManualContactCreate] Cannot save - widget ID missing');
      this.snackBar.open(this.translate.instant('ASTIMAX.CONTACT.WIDGET.FAILED_TO_CREATE_CONTACT'),
          'Close',
          { duration: 3000 }
      );
      return;
    }

    try {
      // Get existing manual contacts for proper ID generation
      const widgetData = this.contactWidgetService.getWidgetData(this.contactWidget._id);
      if (!widgetData) {
        throw new Error('Widget data not found');
      }

      // Create the new manual contact
      const result = await this.contactWidgetService.createAstimaxContactManualContact(
          this.contactName.trim(),
          this.phoneNumber.trim(),
      );

			if (result.success && result?.manualContact) {
				// Add to widget settings through service
				await this.addManualContactToWidget(result.manualContact);

				// Save widget settings to database
				await this.contactWidgetService.saveContactWidgetSettingsToDatabase(
					this.contactWidget._id,
					this.contactWidget
				);
			}

			// Close dialog with result
			this.dialogRef.close({
				widget: this.contactWidget,
				name: this.contactName.trim(),
				phoneNumber: this.phoneNumber.trim(),
				contact: result.manualContact,
				success: result.success
			});
    } catch (error) {
      console.error('[ManualContactCreate] Error creating contact:', error);
      this.snackBar.open(this.translate.instant('ASTIMAX.CONTACT.WIDGET.FAILED_TO_CREATE_CONTACT'),
          'Close',
          { duration: 3000 }
      );
    }
  }

  private async addManualContactToWidget(newContact: ManualContact): Promise<void> {
    if (!this.contactWidget?._id) {
      throw new Error('Widget ID missing');
    }

    // Get current manual contacts from service
    const widgetData = this.contactWidgetService.getWidgetData(this.contactWidget._id);
    if (!widgetData) {
      throw new Error('Widget data not found');
    }

    const currentManualContacts = widgetData.manualContacts;
    const updatedManualContacts = [...currentManualContacts, newContact];

    // Update manual contacts through service
    this.contactWidgetService.updateManualContacts(this.contactWidget._id, updatedManualContacts, true);
  }
}
